import { useMutation, UseMutationOptions, useQuery, useQueryClient } from '@tanstack/react-query';

import { postRequest, getRequest, deleteRequest } from 'services/apiRequests';
import type { StringifiedUUID, APIList } from 'types';
import { CollaboratorRoleAccessType } from 'types';
import { DataObjectPermissionResponse } from 'types/dataObject';
import { APIError } from 'utils/errors';
import { QueryHookOptions } from 'types/ReactQuery';

const apiUrl = `/api/v1/collaboration/collaborators/`;

const permissionsUrl = (modelId: StringifiedUUID, objectId: StringifiedUUID) =>
  `/api/v1/tq/hierarchy-models/${modelId}/objects/${objectId}/`;

const deleteSharedResources = (id: string, csrfToken: string) =>
  deleteRequest<null, null>(`${apiUrl}${id}/`, null, csrfToken);

export enum OBJECT_TYPE {
  TERRITORY = 'territory',
}

export type CreateCollaboratorsRequest = {
  user_ids: StringifiedUUID[];
  object_id: StringifiedUUID;
  object_type: OBJECT_TYPE;
  role: string;
  filter_view?: string;
};

const createCollaborators = (csrfToken: string) => (payload: CreateCollaboratorsRequest) => {
  return postRequest<CreateCollaboratorsRequest, null>(apiUrl, payload, csrfToken);
};

const useCreateCollaboratorsMutation = (
  csrfToken: string,
  options: UseMutationOptions<null, APIError, CreateCollaboratorsRequest> = {},
) => {
  return {
    mutation: useMutation<null, APIError, CreateCollaboratorsRequest>({
      ...options,
      mutationFn: createCollaborators(csrfToken),
    }),
  };
};

export type GetCollaboratorsResponse = {
  resource: {
    object: 'shared_resource';
    id: StringifiedUUID;
    organization_id: StringifiedUUID;
    accessor_id: StringifiedUUID;
    accessor_type: 'user';
    resource_id: StringifiedUUID;
    resource_type: 'territory';
    resource_path: StringifiedUUID[];
    permission_type: 'read';
    created_at: StringifiedUUID;
    updated_at: StringifiedUUID;
  };
  ancestor_name: string | null;
  is_ancestor: boolean;
  accessor: {
    object: 'user';
    id: StringifiedUUID;
    first_name: string;
    last_name: string;
    email: string;
    is_organization_admin: boolean;
  };
  accessor_role:
    | CollaboratorRoleAccessType.COLLABORATOR
    | CollaboratorRoleAccessType.COMMENTER
    | CollaboratorRoleAccessType.APPROVER;
  collaborator_role_id?: StringifiedUUID;
};

const getCollaborators = (territoryId: StringifiedUUID) =>
  getRequest<null, APIList<GetCollaboratorsResponse>>(`${apiUrl}${territoryId}/?resource_type=territory`);

const useGetCollaborators = (
  // eslint-disable-next-line @typescript-eslint/default-param-last
  options: QueryHookOptions<APIList<GetCollaboratorsResponse>, APIError> = {},
  territoryId: StringifiedUUID,
) => {
  const queryClient = useQueryClient();
  const QUERY_KEY = ['collaborators', territoryId];
  const query = useQuery<APIList<GetCollaboratorsResponse>, APIError>({
    ...options,
    queryKey: QUERY_KEY,
    queryFn: () => getCollaborators(territoryId),
  });

  const invalidateQuery = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  return {
    query,
    invalidateQuery,
  };
};

const getDataObjectPermission = (modelId: StringifiedUUID, dataObjectId: StringifiedUUID) => {
  return getRequest<null, DataObjectPermissionResponse>(`${permissionsUrl(modelId, dataObjectId)}permissions/`);
};

const useGetDataObjectPermissionQuery = (
  userId: StringifiedUUID | null,
  modelId: StringifiedUUID | null,
  dataObjectId: StringifiedUUID | null,
) => {
  const QUERY_KEY = [userId, modelId, dataObjectId];
  const queryClient = useQueryClient();

  const query = useQuery<DataObjectPermissionResponse, APIError>({
    queryKey: QUERY_KEY,
    queryFn: () => getDataObjectPermission(modelId as string, dataObjectId as string),
    enabled: !!modelId && !!dataObjectId && !!userId,
  });
  const invalidateQuery = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  return { query, invalidateQuery };
};

export const CollaboratorsService = {
  useCreateCollaboratorsMutation,
  useGetCollaborators,
  deleteSharedResources,
  useGetDataObjectPermissionQuery,
};
